import { ref } from 'vue';
import type { Ref } from 'vue';

export interface LoaderFnArgs {
  search?: string;
}

export interface UseLoaderOptions<T> {
  /** Load immediately on creation */
  eagerLoad?: boolean;
  initialData?: T[];
  load: (args?: LoaderFnArgs) => Promise<T[]>;
}

export interface Loader<T, A extends LoaderFnArgs> {
  data: Ref<T[]>;
  initialData: Ref<T[]>;
  initialDataLoaded: Ref<boolean>;
  load: (args?: A) => Promise<T[]>;
  loading: Ref<boolean>;
  reset: () => void;
}

export function useLoader<T, A extends LoaderFnArgs>(options: UseLoaderOptions<T>): Loader<T, A> {
  const initialDataLoaded = ref(false);
  const initialData = ref<T[]>([]);
  const data = ref<T[]>([]);
  const loading = ref(false);

  // Track the current request ID to ignore stale responses
  let currentRequestId = 0;

  const reset = () => {
    // clears cache and current data; next load() will fetch again
    initialDataLoaded.value = false;
    initialData.value = [];
    data.value = [];
    currentRequestId++;
  };

  const load = async (args?: A | undefined): Promise<T[]> => {
    if (
      initialDataLoaded.value &&
      (!args ||
        !Object.keys(args).length ||
        Object.values(args).every((v) => v === null || v === undefined))
    ) {
      data.value = initialData.value as T[];
      return Promise.resolve(data.value as T[]);
    }

    // Increment request ID to invalidate previous requests
    currentRequestId++;
    const thisRequestId = currentRequestId;

    loading.value = true;
    try {
      const loadedData: T[] = await options.load(args);

      // Only update if this is still the latest request
      if (thisRequestId === currentRequestId) {
        data.value = loadedData;

        if (!initialDataLoaded.value || !initialData.value.length) {
          initialData.value = loadedData;
          initialDataLoaded.value = true;
        }

        loading.value = false;
      }

      return loadedData;
    } catch (err) {
      console.error(err);

      // Only update loading state if this is still the latest request
      if (thisRequestId === currentRequestId) {
        loading.value = false;
      }

      return [];
    } finally {
      loading.value = false;
      if (!initialDataLoaded.value) {
        initialDataLoaded.value = true;
      }
    }
  };

  // Load immediately if eagerLoad is true
  if (options.eagerLoad) {
    load();
  }

  return {
    data: data as Ref<T[]>,
    initialData: initialData as Ref<T[]>,
    initialDataLoaded,
    load,
    loading,
    reset,
  };
}
